// PasswordField.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
// optional: npm i zxcvbn
let zxcvbnLib = null;
(async () => {
  try { zxcvbnLib = (await import('zxcvbn')).default; } catch {}
})();

function randBytes(n) {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return a;
}
function generatePassword(len = 20) {
  const alphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()-_=+[]{};:,.?/';
  const buf = randBytes(len);
  return Array.from(buf, b => alphabet[b % alphabet.length]).join('');
}
const WORDLIST = ['wolke','tür','banane','kalt','fenster','glück','pferd','licht','motor','see','krone','radio','pizza','strahl','honig','aster','nadel','mappe','zebra','kiesel']; // ersetze durch echte Diceware-Liste
function generatePassphrase(words = 5) {
  const buf = randBytes(words);
  const out = [];
  for (let i = 0; i < words; i++) out.push(WORDLIST[buf[i] % WORDLIST.length]);
  return out.join(' ');
}

function basicEstimate(pw, banned = []) {
  if (!pw) return { score: 0, feedback: 'Too short' };
  if (pw.length < 8) return { score: 0, feedback: 'Too short' };
  if (banned.some(x => x && pw.toLowerCase().includes(String(x).toLowerCase())))
    return { score: 0, feedback: 'Contains personal info' };
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].reduce((n, r) => n + (r.test(pw) ? 1 : 0), 0);
  const score = Math.min(4, Math.floor((pw.length - 8) / 4) + classes - 1);
  return { score, feedback: score < 2 ? 'Add length or variety' : '' };
}

export default function PasswordField({
  value,
  onChange,
  onValidityChange,         // FIX A: called with (isValid: bool, issues: string[])
  userContext = {},         // { email, first_name, last_name, tenant }
  minLength = 12,
  i18n = {                  // simple i18n fallback
    strength: ['Very weak','Weak','OK','Strong','Very strong'],
    tips: {
      short: 'Use at least 12 characters.',
      personal: 'Avoid using your name, email or company.',
      variety: 'Add more words or symbols.',
      good: 'Looks good!'
    },
    show: 'Show',
    hide: 'Hide',
    copy: 'Copy',
    copied: 'Copied!',
    suggest: 'Suggest',
    passphrase: 'Passphrase',
    password: 'Password'
  }
}) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const capsRef = useRef(false);

  const banned = useMemo(
    () => [userContext.email, userContext.first_name, userContext.last_name, userContext.tenant].filter(Boolean),
    [userContext]
  );

  const zx = useMemo(() => {
    if (zxcvbnLib) return zxcvbnLib(value || '', banned);
    const e = basicEstimate(value || '', banned);
    return { score: e.score, feedback: { warning: e.feedback, suggestions: [] } };
  }, [value, banned]);

  // FIX A: stable ref to latest callback — avoids stale closure and infinite re-render loop
  const onValidityChangeRef = useRef(onValidityChange);
  useEffect(() => { onValidityChangeRef.current = onValidityChange; });

  // FIX A: notify parent whenever value or strength score changes
  useEffect(() => {
    const cb = onValidityChangeRef.current;
    if (!cb) return;
    const len = value?.length ?? 0;
    const isValid = len >= minLength && zx.score >= 3;
    const issues = [];
    if (len < minLength) issues.push(i18n.tips.short);
    else if (zx.score < 3) issues.push(i18n.tips.variety);
    cb(isValid, issues);
  }, [value, zx.score, minLength]); // eslint-disable-line react-hooks/exhaustive-deps

  // FIX C: cap display score — "Strong"/"Very strong" only shown when length is also met
  const effectiveDisplayScore = (value?.length ?? 0) >= minLength ? zx.score : Math.min(zx.score, 2);
  const scoreLabel = i18n.strength[effectiveDisplayScore] || i18n.strength[0];
  const tip =
    (value?.length < minLength && i18n.tips.short) ||
    (zx.feedback?.warning && (zx.feedback.warning.includes('personal') ? i18n.tips.personal : i18n.tips.variety)) ||
    (zx.score >= 3 ? i18n.tips.good : i18n.tips.variety);

  useEffect(() => {
    const h = e => { capsRef.current = e.getModifierState && e.getModifierState('CapsLock'); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopied(true); setTimeout(() => setCopied(false), 1200);
    } catch {}
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(generatePassword(20))}
          className="px-3 py-1 rounded-md bg-gray-700 text-white hover:bg-gray-600"
          title="Generate strong password"
        >
          {i18n.password}
        </button>
        <div className="grow" />
        <button
          type="button"
          onClick={() => onChange('')}
          className="hidden" aria-hidden // optional clear button
        />
      </div>

      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          className="w-full rounded-lg bg-gray-800 text-white px-3 py-2 outline-none"
          value={value}
          onChange={e => onChange(e.target.value)}
          autoComplete="new-password"
          spellCheck={false}
          placeholder="••••••••••"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2">
          <button
            type="button"
            onClick={() => setVisible(v => !v)}
            className="text-xs text-gray-300 hover:text-white"
          >
            {visible ? i18n.hide : i18n.show}
          </button>
          <button
            type="button"
            onClick={copy}
            disabled={!value}
            className="text-xs text-gray-300 hover:text-white"
          >
            {copied ? i18n.copied : i18n.copy}
          </button>
        </div>
      </div>

      {/* strength meter */}
      <div aria-label="password strength" className="h-2 w-full bg-gray-700 rounded overflow-hidden">
        <div
          className={`h-full transition-all ${
            zx.score <= 1 ? 'bg-red-500' : zx.score === 2 ? 'bg-yellow-500' : 'bg-green-500'
          }`}
          style={{ width: `${(zx.score + 1) * 20}%` }}
        />
      </div>
      <div className="text-xs text-gray-300 flex items-center gap-2">
        <span>{scoreLabel}</span>
        <span>•</span>
        <span>{tip}</span>
        {capsRef.current && <span className="text-orange-400">• Caps Lock</span>}
      </div>

      {/* policy bullet (optional visual) */}
      <ul className="text-xs text-gray-400 list-disc pl-5">
        <li>≥ {minLength} Zeichen (Passphrase empfohlen)</li>
        <li>Keine persönlichen Daten oder einfache Muster</li>
        <li>Leerzeichen & Sonderzeichen erlaubt</li>
      </ul>
    </div>
  );
}
